import { useEffect, useRef, useMemo } from "react";
import styles from "./Innerhtml.module.css"; // Import CSS module
import { getWsrvUrl, getSrcSet } from "@/utils/wsrv";

const Innerhtml = ({ data , lineClamp }) => {
  const mainRef = useRef(null);

  // Pre-process HTML to rewrite <img> tags to use wsrv.nl with srcset
  // This handles server-rendered HTML before hydration for better SEO & LCP
  const processedHtml = useMemo(() => {
    if (!data || typeof data !== "string") return data;
    try {
      // Rewrite img src to wsrv optimized + add srcset/sizes/loading
      return data.replace(/<img([^>]*?)src=["']([^"']+)["']([^>]*?)>/gi, (match, before, src, after) => {
        // Skip data/blob/local
        if (!src.startsWith("http")) return match;
        if (src.includes("wsrv.nl")) return match;

        const widths = [320, 480, 640, 750, 828, 1080, 1200];
        const srcSet = getSrcSet(src, widths, { q: 80, output: "webp" });
        const wsrvSrc = getWsrvUrl(src, { w: 800, q: 80, output: "webp" });

        // If already has srcset, keep original
        if (/srcset/i.test(match)) return match.replace(src, wsrvSrc);

        let attrs = `src="${wsrvSrc}"`;
        if (srcSet) attrs += ` srcset="${srcSet}" sizes="(max-width: 768px) 100vw, 800px"`;
        // Ensure lazy + async decoding unless we add them already
        if (!/loading=/i.test(match)) attrs += ` loading="lazy"`;
        if (!/decoding=/i.test(match)) attrs += ` decoding="async"`;

        return `<img${before} ${attrs} ${after}>`;
      });
    } catch {
      return data;
    }
  }, [data]);

  useEffect(() => {
    const styleTablesAndImages = () => {
      if (mainRef.current) {
        const tables = mainRef.current.querySelectorAll("table");
        const images = mainRef.current.querySelectorAll("img");

        tables.forEach((table) => {
          const parentDiv = document.createElement("div");
          parentDiv.classList.add(styles.tableWrapper);
          table.parentNode.insertBefore(parentDiv, table);
          parentDiv.appendChild(table);
          table.style.width = "100%";
        });

        images.forEach((img) => {
          // Enhance with wsrv if not already optimized
          const origSrc = img.getAttribute("src");
          if (origSrc && origSrc.startsWith("http") && !origSrc.includes("wsrv.nl")) {
            const widths = [320, 480, 640, 750, 828, 1080, 1200];
            const srcSet = getSrcSet(origSrc, widths, { q: 80, output: "webp" });
            const wsrvSrc = getWsrvUrl(origSrc, { w: 800, q: 80, output: "webp" });
            if (wsrvSrc && wsrvSrc !== origSrc) {
              img.src = wsrvSrc;
              if (srcSet) {
                img.srcset = srcSet;
                img.sizes = "(max-width: 768px) 100vw, 800px";
              }
              img.loading = "lazy";
              img.decoding = "async";
            }
          }

          img.removeAttribute("height");
          img.removeAttribute("width");

          // Only wrap if not already wrapped
          if (!img.parentElement?.classList.contains(styles.imageWrapper)) {
            const parentDiv = document.createElement("div");
            parentDiv.classList.add(styles.imageWrapper);
            img.parentNode.insertBefore(parentDiv, img);
            parentDiv.appendChild(img);
            img.style.maxWidth = "100%";
          }
          img.style.height = "auto";
        });
      }
    };

    styleTablesAndImages();
  }, [processedHtml]); // Re-run when data changes

  return (
    <div>
      {processedHtml && (
        <div
          ref={mainRef}
          className={lineClamp? lineClamp: styles.dataContent}
          dangerouslySetInnerHTML={{ __html: processedHtml }}
        />
      )}
    </div>
  );
};

export default Innerhtml;
